"use client";

import React, { Children, ReactElement, ReactNode, ReactPortal } from "react";

import { motion } from "framer-motion";

import { AboutIcons } from "../icons";
import GlowingBadge from "../ui/glowing-badge";

function ContentWithStyles({ children }: { children: ReactNode }) {
  const list = Children.toArray(children);

  if (list.length > 1)
    return Children.map(children, (child) => (
      <ContentWithStyles>{child}</ContentWithStyles>
    ));

  const child = list[0];

  if (!child || typeof child !== "object" || !("props" in child)) return child;

  const element = child as React.JSX.Element;

  if (element.type === "img") {
    return (
      <div className="mt-6 flex w-full justify-center">
        {React.cloneElement(element, {
          className: "w-full rounded-[30px]",
        })}
      </div>
    );
  } else if (element.type === "p") {
    // special case for image as it comes from CMS

    const paragraph = element;
    if (paragraph.props.children?.type === "img")
      return <ContentWithStyles>{paragraph.props.children}</ContentWithStyles>;

    return React.cloneElement(element, {
      className:
        "text-[18px] mb-4 mt-4 text-left text-base font-normal leading-[32px] text-gray-300",
    });
  } else if (element.type === "h2") {
    return React.cloneElement(element, {
      className: "mt-6 text-xl font-semibold",
    });
  } else if (element.type === "ul") {
    return React.cloneElement(element, {
      className: "mt-2 list-disc pl-5 text-gray-300",
    });
  }

  return React.cloneElement(element, {
    children: <ContentWithStyles>{element.props.children}</ContentWithStyles>,
  });
}

const TwoColumnsContentSection = ({
  tag,
  heading,
  children,
}: {
  tag?: ReactNode;
  heading?: string;
  children?: ReactNode;
}) => {
  return (
    <motion.section
      className="module-two-columns-content mx-auto max-w-1440 px-6 lg:px-24"
      initial={{ opacity: 0, y: 100 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="flex flex-col justify-between gap-10 md:items-center lg:flex-row lg:items-start">
        <div className="max-w-[511px]">
          <div className="mb-4 flex lg:mb-3">
            <GlowingBadge>
              <AboutIcons.mission />
              <span>{tag}</span>
            </GlowingBadge>
          </div>
          <h2 className="text-3xl font-semibold !leading-[1.3] md:text-5xl lg:text-5xl lg:leading-[1.4]">
            {heading}
          </h2>
        </div>
        <div className="flex max-w-[501px] flex-col gap-6 text-base !leading-[1.5] text-white/60 md:gap-8 md:text-lg">
          <ContentWithStyles>{children}</ContentWithStyles>
        </div>
      </div>
    </motion.section>
  );
};

export default TwoColumnsContentSection;
